import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link2, Unlink, FileText, CheckCircle, AlertCircle, UserPlus, AlertTriangle, ShieldAlert, RotateCcw } from 'lucide-react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { EmptyState } from '../components/ui/EmptyState';
import { useToast } from '../components/ui/Toast';
import { usePermissions } from '../hooks/usePermissions';

const SOURCE_LABELS: Record<string, string> = {
  estate_document: 'Estate',
  insurance_policy: 'Insurance',
  account: 'Account',
  manual: 'Manual',
};

const PRIORITY_OPTIONS = [
  { value: 'primary', label: 'Primary' },
  { value: 'contingent', label: 'Contingent' },
  { value: 'unspecified', label: 'Unspecified' },
];

const PRIORITY_COLORS: Record<string, string> = {
  primary: 'bg-blue-100 text-blue-700',
  contingent: 'bg-purple-100 text-purple-700',
  unspecified: 'bg-gray-100 text-gray-500',
};

const RELATIONSHIP_OPTIONS = [
  { value: 'self', label: 'Self' },
  { value: 'spouse', label: 'Spouse' },
  { value: 'child', label: 'Child' },
  { value: 'parent', label: 'Parent' },
  { value: 'dependent_other', label: 'Other Dependent' },
];

/** Normalize a name for duplicate comparison: trim, collapse spaces, lowercase */
function normalizeName(name: string): string {
  return name.trim().replace(/\s+/g, ' ').toLowerCase();
}

export function BeneficiaryReviewPage() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { can } = usePermissions();
  const canWrite = can('snapshots:write');
  const [linkTarget, setLinkTarget] = useState<string | null>(null);
  const [createTarget, setCreateTarget] = useState<string | null>(null);
  const [createName, setCreateName] = useState('');
  // Backend-detected duplicate: { benId, member: { id, name, relationship } }
  const [serverDuplicate, setServerDuplicate] = useState<{ benId: string; member: { id: string; name: string; relationship: string } } | null>(null);
  const [createRelationship, setCreateRelationship] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['beneficiaryReview'],
    queryFn: () => api.getBeneficiaryReview(),
    refetchInterval: (query) => {
      // Poll while any invalid extracted document is in processing state
      const inv = query.state.data?.invalidContextBeneficiaries;
      if (inv?.some((b: any) => b.documentStatus === 'processing')) return 3000;
      return false;
    },
  });

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: ['beneficiaryReview'] });
    queryClient.invalidateQueries({ queryKey: ['unresolvedBeneficiaries'] });
    queryClient.invalidateQueries({ queryKey: ['profileCompleteness'] });
    queryClient.invalidateQueries({ queryKey: ['nextActions'] });
  };

  const guardWrite = <T,>(fn: (args: T) => Promise<any>) =>
    (args: T) => { if (!canWrite) return Promise.reject(new Error('Permission denied')); return fn(args); };

  const linkMutation = useMutation({
    mutationFn: guardWrite(({ benId, memberId }: { benId: string; memberId: string }) =>
      api.linkBeneficiary(benId, memberId)),
    onSuccess: () => {
      invalidateAll();
      toast.success('Beneficiary linked');
      setLinkTarget(null);
    },
    onError: () => toast.error('Failed to link beneficiary'),
  });

  const unlinkMutation = useMutation({
    mutationFn: (benId: string) => api.unlinkBeneficiary(benId),
    onSuccess: () => {
      invalidateAll();
      toast.success('Beneficiary unlinked');
    },
    onError: () => toast.error('Failed to unlink beneficiary'),
  });

  const createAndLinkMutation = useMutation({
    mutationFn: async ({ benId, name, relationship }: { benId: string; name: string; relationship: string }) => {
      const member = await api.addHouseholdMember({ name, relationship });
      await api.linkBeneficiary(benId, member.id);
      return member;
    },
    onSuccess: (member) => {
      invalidateAll();
      toast.success(`Created "${member.name}" and linked beneficiary`);
      setCreateTarget(null);
      setCreateName('');
      setCreateRelationship('');
    },
    onError: (error: any) => {
      // Catch backend 409 duplicate detection
      if (error?.response?.status === 409 && error?.response?.data?.errorCode === 'HOUSEHOLD_MEMBER_DUPLICATE') {
        const existing = error.response.data.existingMember;
        if (existing && createTarget) {
          setServerDuplicate({ benId: createTarget, member: existing });
          return;
        }
      }
      toast.error('Failed to create member');
    },
  });

  const priorityMutation = useMutation({
    mutationFn: ({ benId, priorityType }: { benId: string; priorityType: string }) =>
      api.updateBeneficiaryPriority(benId, priorityType),
    onSuccess: () => {
      invalidateAll();
      toast.success('Priority updated');
    },
    onError: () => toast.error('Failed to update priority'),
  });

  // Manual beneficiary CRUD
  const [addManualForm, setAddManualForm] = useState<{ accountId?: string; relatedEstateDocumentId?: string; sourceDocumentId?: string } | null>(null);
  const [addManualName, setAddManualName] = useState('');
  const [addManualPriority, setAddManualPriority] = useState('primary');
  const [editManualId, setEditManualId] = useState<string | null>(null);
  const [editManualName, setEditManualName] = useState('');
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [reprocessConfirmDocId, setReprocessConfirmDocId] = useState<string | null>(null);
  const [reprocessedDocIds, setReprocessedDocIds] = useState<Set<string>>(new Set());

  const createManualMutation = useMutation({
    mutationFn: (data: Parameters<typeof api.createManualBeneficiary>[0]) => api.createManualBeneficiary(data),
    onSuccess: () => {
      invalidateAll();
      toast.success('Beneficiary added');
      setAddManualForm(null);
      setAddManualName('');
      setAddManualPriority('primary');
    },
    onError: () => toast.error('Failed to add beneficiary'),
  });

  const updateManualMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof api.updateManualBeneficiary>[1] }) =>
      api.updateManualBeneficiary(id, data),
    onSuccess: () => {
      invalidateAll();
      toast.success('Beneficiary updated');
      setEditManualId(null);
    },
    onError: () => toast.error('Failed to update beneficiary'),
  });

  const deleteManualMutation = useMutation({
    mutationFn: (id: string) => api.deleteManualBeneficiary(id),
    onSuccess: () => {
      invalidateAll();
      toast.success('Beneficiary removed');
    },
    onError: () => toast.error('Failed to remove beneficiary'),
  });

  const retryDocMutation = useMutation({
    mutationFn: (docId: string) => api.retryDocument(docId),
    onSuccess: (data: any, docId: string) => {
      setReprocessedDocIds((prev) => new Set([...prev, docId]));
      invalidateAll();
      queryClient.invalidateQueries({ queryKey: ['documents'] });
      toast.success(data.message ?? 'Reprocessing started');
    },
    onError: (error: any) => toast.error(error?.response?.data?.message ?? 'Reprocess failed'),
  });

  const startCreate = (benId: string, displayName: string) => {
    setLinkTarget(null);
    setCreateTarget(benId);
    setCreateName(displayName);
    setCreateRelationship('');
    setServerDuplicate(null);
  };

  if (isLoading || !data) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const { unresolvedBeneficiaries, linkedBeneficiaries, householdMembers, consistencySummary } = data;
  const totalCount = unresolvedBeneficiaries.length + linkedBeneficiaries.length;

  // Duplicate detection: find existing member matching normalized name + relationship
  const findDuplicate = (name: string, relationship: string) => {
    if (!name.trim() || !relationship) return null;
    const normalized = normalizeName(name);
    return householdMembers.find(
      (m: any) => normalizeName(m.name) === normalized && m.relationship === relationship
    ) ?? null;
  };

  const duplicateMatch = createTarget ? findDuplicate(createName, createRelationship) : null;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Beneficiary Review</h1>
        <p className="text-gray-600">
          Link beneficiary names from your documents to household members for accurate cross-domain alignment
        </p>
      </div>

      {/* Consistency Signals */}
      {consistencySummary && totalCount > 0 && (
        <div className="bg-white rounded-lg border px-4 py-3">
          <div className="flex items-center gap-2 mb-2">
            <ShieldAlert className="h-4 w-4 text-gray-400" />
            <h2 className="text-sm font-semibold text-gray-700">Consistency Signals</h2>
          </div>
          <div className="flex flex-wrap gap-2">
            <Signal label="Estate beneficiaries" ok={consistencySummary.estateCaptured} />
            <Signal label="Insurance beneficiaries" ok={consistencySummary.insuranceCaptured} />
            <Signal label="Account beneficiaries" ok={consistencySummary.accountCaptured} />
            {consistencySummary.unresolvedCount > 0 && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-orange-50 text-orange-700">
                <AlertCircle className="h-3 w-3" /> Unresolved: {consistencySummary.unresolvedCount}
              </span>
            )}
            {consistencySummary.crossSourceMismatch && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-red-50 text-red-700">
                <AlertTriangle className="h-3 w-3" /> Primary mismatch detected
              </span>
            )}
            {consistencySummary.contingentMismatch && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-yellow-50 text-yellow-700">
                <AlertTriangle className="h-3 w-3" /> Contingent mismatch detected
              </span>
            )}
            {consistencySummary.needsPriorityReview && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-gray-200 text-gray-700">
                <AlertCircle className="h-3 w-3" /> {consistencySummary.unspecifiedCount} need{consistencySummary.unspecifiedCount === 1 ? 's' : ''} priority classification
              </span>
            )}
            {consistencySummary.hasInvalidContext && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-red-100 text-red-800" data-testid="invalid-context-chip">
                <AlertTriangle className="h-3 w-3" /> {consistencySummary.invalidContextCount} with invalid context
              </span>
            )}
            {consistencySummary.hasInvalidExtracted && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-orange-100 text-orange-800" data-testid="invalid-extracted-chip">
                <AlertTriangle className="h-3 w-3" /> {consistencySummary.invalidExtractedCount} extracted with bad context
              </span>
            )}
            {(consistencySummary.needsFactReviewDocumentCount > 0) && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-blue-50 text-blue-700" data-testid="fact-review-chip">
                <FileText className="h-3 w-3" /> {consistencySummary.needsFactReviewDocumentCount} awaiting fact review
              </span>
            )}
          </div>
          {consistencySummary.mismatchDetails && (
            <>
              <MismatchBlock label="Primary" pairs={consistencySummary.mismatchDetails.primary} targetLabels={consistencySummary.targetContexts} onAddManual={(ctx) => { setAddManualForm(ctx); setAddManualPriority('primary'); }} />
              <MismatchBlock label="Contingent" pairs={consistencySummary.mismatchDetails.contingent} targetLabels={consistencySummary.targetContexts} onAddManual={(ctx) => { setAddManualForm(ctx); setAddManualPriority('contingent'); }} />
            </>
          )}
          {/* Inline manual add form */}
          {addManualForm && (
            <div className="mt-2 pt-2 border-t border-gray-100 flex items-center gap-2">
              <input
                type="text"
                value={addManualName}
                onChange={(e) => setAddManualName(e.target.value)}
                placeholder="Beneficiary name"
                className="border border-gray-300 rounded px-2 py-1 text-xs flex-1"
              />
              <select value={addManualPriority} onChange={(e) => setAddManualPriority(e.target.value)} className="border border-gray-300 rounded px-2 py-1 text-xs">
                {PRIORITY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <button
                className="px-2.5 py-1 text-xs font-medium text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50"
                disabled={!addManualName.trim() || createManualMutation.isPending}
                onClick={() => createManualMutation.mutate({ beneficiaryDisplayName: addManualName.trim(), priorityType: addManualPriority, ...addManualForm })}
              >
                {createManualMutation.isPending ? 'Adding...' : 'Add'}
              </button>
              <button className="text-xs text-gray-400 hover:text-gray-600" onClick={() => setAddManualForm(null)}>Cancel</button>
            </div>
          )}
        </div>
      )}

      {totalCount === 0 && !(data.unattachedManualBeneficiaries?.length > 0) && !(data.invalidContextBeneficiaries?.length > 0) && !(data.remediationDocuments?.some((d: any) => d.remediationOutcome === 'resolved_needs_review')) ? (
        <EmptyState
          icon={CheckCircle}
          title="No beneficiary designations yet"
          description="Upload estate documents, insurance policies, or account statements with beneficiary information."
          action={{ label: 'Upload Documents', href: '/documents' }}
        />
      ) : (
        <>
          {/* Unresolved Section */}
          <div className="bg-white rounded-lg border">
            <div className="px-4 py-3 border-b flex items-center gap-2">
              <AlertCircle className="h-4 w-4 text-orange-500" />
              <h2 className="text-sm font-semibold text-gray-700">
                Unresolved ({unresolvedBeneficiaries.length})
              </h2>
            </div>
            {unresolvedBeneficiaries.length === 0 ? (
              <div className="px-4 py-6 text-center text-sm text-gray-500">
                All beneficiaries are linked. Nice work.
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {unresolvedBeneficiaries.map((ben: any) => (
                  <div key={ben.id} id={`ben-${ben.id}`} className="px-4 py-3 transition-all">
                    <div className="flex items-center gap-3">
                      <div className="flex-1 min-w-0">
                        {editManualId === ben.id ? (
                          <div className="flex items-center gap-2">
                            <input type="text" value={editManualName} onChange={(e) => setEditManualName(e.target.value)} className="border border-gray-300 rounded px-2 py-1 text-sm flex-1" />
                            <button className="text-xs text-blue-600 hover:underline" onClick={() => { updateManualMutation.mutate({ id: ben.id, data: { beneficiaryDisplayName: editManualName } }); }}>Save</button>
                            <button className="text-xs text-gray-400 hover:text-gray-600" onClick={() => setEditManualId(null)}>Cancel</button>
                          </div>
                        ) : (
                          <p className="text-sm font-medium text-gray-900">{ben.beneficiaryDisplayName}</p>
                        )}
                        <div className="flex items-center gap-2 text-xs text-gray-500 mt-0.5">
                          <span className={`px-1.5 py-0.5 rounded ${ben.sourceType === 'manual' ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-100'}`}>{SOURCE_LABELS[ben.sourceType] ?? ben.sourceType}</span>
                          <PrioritySelect benId={ben.id} value={ben.priorityType} onUpdate={priorityMutation.mutate} />
                          {ben.sourceDocumentId && (
                            <Link to={`/documents/${ben.sourceDocumentId}`} className="text-blue-500 hover:underline flex items-center gap-0.5">
                              <FileText className="h-3 w-3" /> Source
                            </Link>
                          )}
                        </div>
                      </div>
                      {linkTarget !== ben.id && createTarget !== ben.id && (
                        <div className="flex items-center gap-1.5">
                          <button
                            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-blue-600 bg-blue-50 rounded hover:bg-blue-100"
                            onClick={() => { setCreateTarget(null); setLinkTarget(ben.id); }}
                          >
                            <Link2 className="h-3 w-3" /> Link
                          </button>
                          <button
                            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-green-600 bg-green-50 rounded hover:bg-green-100"
                            onClick={() => startCreate(ben.id, ben.beneficiaryDisplayName)}
                          >
                            <UserPlus className="h-3 w-3" /> Create
                          </button>
                          {ben.sourceType === 'manual' && (
                            <>
                              <button className="text-xs text-gray-500 hover:text-blue-600" onClick={() => { setEditManualId(ben.id); setEditManualName(ben.beneficiaryDisplayName); }}>Edit</button>
                              <button className="text-xs text-gray-500 hover:text-red-600" data-testid={`delete-${ben.id}`} onClick={() => setDeleteConfirmId(ben.id)}>Delete</button>
                            </>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Link to existing member */}
                    {linkTarget === ben.id && (
                      <div className="mt-2 flex items-center gap-2">
                        <select
                          className="border border-gray-300 rounded px-2 py-1 text-sm flex-1"
                          defaultValue=""
                          onChange={(e) => {
                            if (e.target.value) linkMutation.mutate({ benId: ben.id, memberId: e.target.value });
                          }}
                        >
                          <option value="" disabled>Select household member...</option>
                          {householdMembers.map((m: any) => (
                            <option key={m.id} value={m.id}>{m.name} ({m.relationship})</option>
                          ))}
                        </select>
                        <button className="text-xs text-gray-400 hover:text-gray-600" onClick={() => setLinkTarget(null)}>Cancel</button>
                      </div>
                    )}

                    {/* Create new member and link */}
                    {createTarget === ben.id && (
                      <CreateAndLinkForm
                        benId={ben.id}
                        createName={createName}
                        setCreateName={setCreateName}
                        createRelationship={createRelationship}
                        setCreateRelationship={setCreateRelationship}
                        duplicateMatch={duplicateMatch}
                        serverDuplicate={serverDuplicate && serverDuplicate.benId === ben.id ? serverDuplicate.member : null}
                        onLink={(memberId: string) => {
                          linkMutation.mutate({ benId: ben.id, memberId });
                          setCreateTarget(null);
                          setCreateName('');
                          setCreateRelationship('');
                          setServerDuplicate(null);
                        }}
                        onCreateAndLink={() => createAndLinkMutation.mutate({
                          benId: ben.id,
                          name: createName.trim(),
                          relationship: createRelationship,
                        })}
                        isCreating={createAndLinkMutation.isPending}
                        onCancel={() => { setCreateTarget(null); setServerDuplicate(null); }}
                      />
                    )}

                    {/* Delete confirmation for manual rows */}
                    <DeleteConfirm
                      benId={ben.id}
                      isTarget={deleteConfirmId === ben.id}
                      onConfirm={() => { deleteManualMutation.mutate(ben.id); setDeleteConfirmId(null); }}
                      onCancel={() => setDeleteConfirmId(null)}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Linked Section */}
          <div className="bg-white rounded-lg border">
            <div className="px-4 py-3 border-b flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-green-500" />
              <h2 className="text-sm font-semibold text-gray-700">
                Linked ({linkedBeneficiaries.length})
              </h2>
            </div>
            {linkedBeneficiaries.length === 0 ? (
              <div className="px-4 py-6 text-center text-sm text-gray-500">
                No beneficiaries have been linked yet.
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {linkedBeneficiaries.map((ben: any) => (
                  <div key={ben.id} id={`ben-${ben.id}`} className="px-4 py-3 flex items-center gap-3 transition-all">
                    <div className="flex-1 min-w-0">
                      {editManualId === ben.id ? (
                        <div className="flex items-center gap-2">
                          <input type="text" value={editManualName} onChange={(e) => setEditManualName(e.target.value)} className="border border-gray-300 rounded px-2 py-1 text-sm flex-1" />
                          <button className="text-xs text-blue-600 hover:underline" onClick={() => { updateManualMutation.mutate({ id: ben.id, data: { beneficiaryDisplayName: editManualName } }); }}>Save</button>
                          <button className="text-xs text-gray-400 hover:text-gray-600" onClick={() => setEditManualId(null)}>Cancel</button>
                        </div>
                      ) : (
                        <p className="text-sm font-medium text-gray-900">
                          {ben.beneficiaryDisplayName}
                          <span className="ml-2 text-green-600 text-xs">
                            → {ben.linkedMemberName} ({ben.linkedMemberRelationship})
                          </span>
                        </p>
                      )}
                      <div className="flex items-center gap-2 text-xs text-gray-500 mt-0.5">
                        <span className={`px-1.5 py-0.5 rounded ${ben.sourceType === 'manual' ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-100'}`}>{SOURCE_LABELS[ben.sourceType] ?? ben.sourceType}</span>
                        <PrioritySelect benId={ben.id} value={ben.priorityType} onUpdate={priorityMutation.mutate} />
                        {ben.sourceDocumentId && (
                          <Link to={`/documents/${ben.sourceDocumentId}`} className="text-blue-500 hover:underline flex items-center gap-0.5">
                            <FileText className="h-3 w-3" /> Source
                          </Link>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <button
                        className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-red-600 bg-red-50 rounded hover:bg-red-100"
                        onClick={() => unlinkMutation.mutate(ben.id)}
                        disabled={unlinkMutation.isPending}
                      >
                        <Unlink className="h-3 w-3" /> Unlink
                      </button>
                      {ben.sourceType === 'manual' && (
                        <>
                          <button className="text-xs text-gray-500 hover:text-blue-600" onClick={() => { setEditManualId(ben.id); setEditManualName(ben.beneficiaryDisplayName); }}>Edit</button>
                          <button className="text-xs text-gray-500 hover:text-red-600" data-testid={`delete-${ben.id}`} onClick={() => setDeleteConfirmId(ben.id)}>Delete</button>
                        </>
                      )}
                    </div>
                    <DeleteConfirm
                      benId={ben.id}
                      isTarget={deleteConfirmId === ben.id}
                      onConfirm={() => { deleteManualMutation.mutate(ben.id); setDeleteConfirmId(null); }}
                      onCancel={() => setDeleteConfirmId(null)}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Unattached Manual Beneficiaries */}
          {data.unattachedManualBeneficiaries && data.unattachedManualBeneficiaries.length > 0 && (
            <div className="bg-white rounded-lg border" data-testid="unattached-manual">
              <div className="px-4 py-3 border-b flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-yellow-500" />
                <h2 className="text-sm font-semibold text-gray-700">
                  Unattached Manual ({data.unattachedManualBeneficiaries.length})
                </h2>
              </div>
              <p className="px-4 py-1.5 text-xs text-gray-500">
                These manual entries have no target context and are excluded from consistency comparison.
              </p>
              <div className="divide-y divide-gray-100">
                {data.unattachedManualBeneficiaries.map((ben: any) => (
                  <div key={ben.id} id={`ben-${ben.id}`} className="px-4 py-3 flex items-center gap-3 transition-all">
                    <div className="flex-1 min-w-0">
                      {editManualId === ben.id ? (
                        <div className="flex items-center gap-2">
                          <input type="text" value={editManualName} onChange={(e) => setEditManualName(e.target.value)} className="border border-gray-300 rounded px-2 py-1 text-sm flex-1" />
                          <button className="text-xs text-blue-600 hover:underline" onClick={() => updateManualMutation.mutate({ id: ben.id, data: { beneficiaryDisplayName: editManualName } })}>Save</button>
                          <button className="text-xs text-gray-400 hover:text-gray-600" onClick={() => setEditManualId(null)}>Cancel</button>
                        </div>
                      ) : (
                        <p className="text-sm font-medium text-gray-900">{ben.beneficiaryDisplayName}</p>
                      )}
                      <div className="flex items-center gap-2 text-xs text-gray-500 mt-0.5">
                        <span className="px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700">Manual</span>
                        <PrioritySelect benId={ben.id} value={ben.priorityType} onUpdate={priorityMutation.mutate} />
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <button className="text-xs text-gray-500 hover:text-blue-600" onClick={() => { setEditManualId(ben.id); setEditManualName(ben.beneficiaryDisplayName); }}>Edit</button>
                      <button className="text-xs text-gray-500 hover:text-red-600" onClick={() => setDeleteConfirmId(ben.id)}>Delete</button>
                    </div>
                    <DeleteConfirm
                      benId={ben.id}
                      isTarget={deleteConfirmId === ben.id}
                      onConfirm={() => { deleteManualMutation.mutate(ben.id); setDeleteConfirmId(null); }}
                      onCancel={() => setDeleteConfirmId(null)}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Invalid Context Beneficiaries */}
          {data.invalidContextBeneficiaries && data.invalidContextBeneficiaries.length > 0 && (() => {
            const manualInvalid = data.invalidContextBeneficiaries.filter((b: any) => b.sourceType === 'manual');
            const extractedInvalid = data.invalidContextBeneficiaries.filter((b: any) => b.sourceType !== 'manual');
            return (
            <div className="bg-white rounded-lg border border-red-200" data-testid="invalid-context-section">
              <div className="px-4 py-3 border-b border-red-200 flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-red-500" />
                <h2 className="text-sm font-semibold text-red-700">
                  Invalid Context ({data.invalidContextBeneficiaries.length})
                </h2>
              </div>

              {/* Manual invalid rows — repairable */}
              {manualInvalid.length > 0 && (
                <div data-testid="invalid-manual-subsection">
                  <p className="px-4 py-1.5 text-xs text-red-600">
                    Manual rows with multiple contexts — choose one or clear all.
                  </p>
                  <div className="divide-y divide-red-100">
                    {manualInvalid.map((ben: any) => (
                      <div key={ben.id} id={`ben-${ben.id}`} className="px-4 py-3 transition-all">
                        <div className="flex items-center gap-3">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900">{ben.beneficiaryDisplayName}</p>
                            <div className="flex items-center gap-2 text-xs text-gray-500 mt-0.5">
                              <span className="px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700">Manual</span>
                              {ben.accountId && <span className="px-1.5 py-0.5 rounded bg-gray-100">Account</span>}
                              {ben.relatedEstateDocumentId && <span className="px-1.5 py-0.5 rounded bg-gray-100">Estate</span>}
                              {ben.sourceDocumentId && <span className="px-1.5 py-0.5 rounded bg-gray-100">Insurance</span>}
                            </div>
                          </div>
                          <div className="flex items-center gap-1.5">
                            {ben.accountId && <button className="text-xs text-blue-600 hover:underline" onClick={() => updateManualMutation.mutate({ id: ben.id, data: { relatedEstateDocumentId: null, sourceDocumentId: null } })}>Keep Account</button>}
                            {ben.relatedEstateDocumentId && <button className="text-xs text-blue-600 hover:underline" onClick={() => updateManualMutation.mutate({ id: ben.id, data: { accountId: null, sourceDocumentId: null } })}>Keep Estate</button>}
                            {ben.sourceDocumentId && <button className="text-xs text-blue-600 hover:underline" onClick={() => updateManualMutation.mutate({ id: ben.id, data: { accountId: null, relatedEstateDocumentId: null } })}>Keep Insurance</button>}
                            <button className="text-xs text-gray-500 hover:underline" onClick={() => updateManualMutation.mutate({ id: ben.id, data: { accountId: null, relatedEstateDocumentId: null, sourceDocumentId: null } })}>Clear all</button>
                            <button className="text-xs text-gray-500 hover:text-red-600" onClick={() => setDeleteConfirmId(ben.id)}>Delete</button>
                          </div>
                        </div>
                        <DeleteConfirm benId={ben.id} isTarget={deleteConfirmId === ben.id} onConfirm={() => { deleteManualMutation.mutate(ben.id); setDeleteConfirmId(null); }} onCancel={() => setDeleteConfirmId(null)} />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Extracted invalid rows — driven by backend remediationDocuments */}
              {extractedInvalid.length > 0 && (() => {
                const invalidGroups = (data.remediationDocuments ?? []).filter((d: any) => d.stillInvalid);
                // Build row lookup by ID for explicit membership
                const rowById = new Map(extractedInvalid.map((b: any) => [b.id, b]));
                const coveredIds = new Set(invalidGroups.flatMap((g: any) => g.invalidRowIds ?? []));
                const orphaned = extractedInvalid.filter((b: any) => !coveredIds.has(b.id));

                const docGroups: any[] = invalidGroups.map((g: any) => ({
                  ...g,
                  docId: g.documentId,
                  rows: (g.invalidRowIds ?? []).map((id: string) => rowById.get(id)).filter(Boolean),
                }));
                return (
                <div data-testid="invalid-extracted-subsection">
                  <p className="px-4 py-1.5 text-xs text-orange-600 border-t border-red-100">
                    Extracted rows with conflicting contexts — reprocess or review the source document.
                  </p>
                  {docGroups.map((group) => (
                    <div key={group.docId} className="px-4 py-2 border-t border-red-50" data-testid={`doc-group-${group.docId}`}>
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2 text-xs">
                          <FileText className="h-3.5 w-3.5 text-gray-400" />
                          <span className="font-medium text-gray-700">{group.title}</span>
                          <span className="text-gray-400">({group.rowCount || group.rows.length} row{(group.rowCount || group.rows.length) > 1 ? 's' : ''})</span>
                          <span className={`px-1.5 py-0.5 rounded ${
                            group.remediationOutcome === 'still_processing' ? 'bg-blue-100 text-blue-700' :
                            group.remediationOutcome === 'failed_again' ? 'bg-red-100 text-red-700' :
                            group.remediationOutcome === 'still_invalid_needs_review' ? 'bg-orange-100 text-orange-700' :
                            'bg-gray-100 text-gray-500'
                          }`} data-testid={`outcome-${group.docId}`}>{
                            group.remediationOutcome === 'still_processing' ? 'processing' :
                            group.remediationOutcome === 'failed_again' ? 'failed' :
                            group.remediationOutcome === 'still_invalid_needs_review' ? 'needs review' :
                            'reprocessable'
                          }</span>
                          {group.pendingFactCount > 0 && <span className="px-1.5 py-0.5 rounded bg-blue-50 text-blue-600">{group.pendingFactCount} facts pending</span>}
                          {group.previouslyReviewed && <span className="px-1.5 py-0.5 rounded bg-yellow-50 text-yellow-700">previously reviewed</span>}
                        </div>
                        <div className="flex items-center gap-2">
                          {/* Reprocess action: available for failed_again and reprocessable outcomes */}
                          {group.reprocessable && group.remediationOutcome !== 'still_processing' && reprocessConfirmDocId !== group.docId && (
                            <button
                              className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium text-orange-700 bg-orange-50 rounded hover:bg-orange-100 disabled:opacity-50"
                              disabled={retryDocMutation.isPending}
                              onClick={() => group.previouslyReviewed ? setReprocessConfirmDocId(group.docId) : retryDocMutation.mutate(group.docId)}
                              data-testid={`reprocess-${group.docId}`}
                            >
                              <RotateCcw className={`h-3 w-3 ${retryDocMutation.isPending ? 'animate-spin' : ''}`} />
                              {retryDocMutation.isPending ? 'Reprocessing...' : 'Reprocess document'}
                            </button>
                          )}
                          {reprocessConfirmDocId === group.docId && (
                            <div className="flex items-center gap-1.5 text-xs" data-testid={`reprocess-confirm-${group.docId}`}>
                              <span className="text-orange-700">Will re-extract facts and require re-review.</span>
                              <button className="px-2 py-0.5 text-white bg-orange-600 rounded hover:bg-orange-700" onClick={() => { retryDocMutation.mutate(group.docId); setReprocessConfirmDocId(null); }}>Confirm</button>
                              <button className="text-gray-500 hover:text-gray-700" onClick={() => setReprocessConfirmDocId(null)}>Cancel</button>
                            </div>
                          )}
                          {/* Processing spinner */}
                          {group.remediationOutcome === 'still_processing' && (
                            <span className="inline-flex items-center gap-1 text-xs text-blue-600">
                              <RotateCcw className="h-3 w-3 animate-spin" /> Processing...
                            </span>
                          )}
                          {/* Review action: available for still_invalid_needs_review */}
                          {group.remediationOutcome === 'still_invalid_needs_review' && (
                            <Link to={`/documents/${group.docId}`} className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium text-blue-600 bg-blue-50 rounded hover:bg-blue-100" data-testid={`review-doc-${group.docId}`}>
                              <FileText className="h-3 w-3" /> Review facts
                            </Link>
                          )}
                          <Link to={`/documents/${group.docId}`} className="text-xs text-blue-500 hover:underline flex items-center gap-0.5">
                            <FileText className="h-3 w-3" /> View source
                          </Link>
                        </div>
                      </div>
                      <div className="ml-5 space-y-0.5">
                        {group.rows.map((ben: any) => (
                          <div key={ben.id} id={`ben-${ben.id}`} className="flex items-center gap-2 text-xs text-gray-600 transition-all">
                            <span>{ben.beneficiaryDisplayName}</span>
                            <span className="px-1 py-0.5 rounded bg-gray-100 text-gray-500">{SOURCE_LABELS[ben.sourceType] ?? ben.sourceType}</span>
                            {ben.accountId && <span className="px-1 py-0.5 rounded bg-gray-100">Account</span>}
                            {ben.relatedEstateDocumentId && <span className="px-1 py-0.5 rounded bg-gray-100">Estate</span>}
                            {ben.sourceDocumentId && <span className="px-1 py-0.5 rounded bg-gray-100">Insurance</span>}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                  {orphaned.map((ben: any) => (
                    <div key={ben.id} id={`ben-${ben.id}`} className="px-4 py-2 flex items-center gap-2 text-xs text-gray-600 transition-all">
                      <span>{ben.beneficiaryDisplayName}</span>
                      <span className="px-1 py-0.5 rounded bg-gray-100">{SOURCE_LABELS[ben.sourceType] ?? ben.sourceType}</span>
                    </div>
                  ))}
                </div>
                );
              })()}
              </div>
            );
          })()}
          {/* Post-Reprocess: Resolved documents needing fact review (backend-driven + local bridge) */}
          {(() => {
            // Backend-driven: resolved_needs_review documents
            const backendResolved = (data.remediationDocuments ?? []).filter((d: any) => d.remediationOutcome === 'resolved_needs_review');
            const backendResolvedIds = new Set(backendResolved.map((d: any) => d.documentId));

            // Local bridge: docs reprocessed from this page that are neither in invalid bucket nor in backend resolved
            const currentInvalidDocIds = new Set(
              (data.invalidContextBeneficiaries ?? [])
                .filter((b: any) => b.sourceType !== 'manual' && b.sourceDocumentId)
                .map((b: any) => b.sourceDocumentId)
            );
            const bridgedIds = [...reprocessedDocIds].filter((id) => !currentInvalidDocIds.has(id) && !backendResolvedIds.has(id));

            // Combine: backend resolved + bridge (deduplicated)
            const allResolved = [
              ...backendResolved.map((d: any) => ({ docId: d.documentId, title: d.title, pendingFacts: d.pendingFactCount, fromBackend: true })),
              ...bridgedIds.map((id) => ({ docId: id, title: id, pendingFacts: 0, fromBackend: false })),
            ];

            if (allResolved.length === 0) return null;

            const handleReviewClick = (docId: string) => {
              setReprocessedDocIds((prev) => { const next = new Set(prev); next.delete(docId); return next; });
            };

            return (
              <div className="bg-white rounded-lg border border-green-200" data-testid="needs-fact-review-section">
                <div className="px-4 py-3 border-b border-green-200 flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  <h2 className="text-sm font-semibold text-green-700">
                    Context Resolved — Review Facts ({allResolved.length})
                  </h2>
                </div>
                <p className="px-4 py-1.5 text-xs text-green-600">
                  Reprocessing fixed the structural issue. Review the refreshed extracted facts to complete remediation.
                </p>
                <div className="divide-y divide-green-100">
                  {allResolved.map((doc) => (
                    <div key={doc.docId} className="px-4 py-2 flex items-center justify-between">
                      <div className="flex items-center gap-2 text-xs">
                        <FileText className="h-3.5 w-3.5 text-gray-400" />
                        <span className="font-medium text-gray-700">{doc.title}</span>
                        <span className="px-1.5 py-0.5 rounded bg-green-50 text-green-600">context resolved</span>
                        {doc.pendingFacts > 0 && <span className="px-1.5 py-0.5 rounded bg-blue-50 text-blue-600">{doc.pendingFacts} facts pending</span>}
                      </div>
                      <Link
                        to={`/documents/${doc.docId}`}
                        className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-blue-600 bg-blue-50 rounded hover:bg-blue-100"
                        data-testid={`review-facts-${doc.docId}`}
                        onClick={() => handleReviewClick(doc.docId)}
                      >
                        <FileText className="h-3 w-3" /> Review refreshed facts
                      </Link>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}
        </>
      )}
    </div>
  );
}

// =============================================================================
// MismatchBlock — expandable mismatch detail per priority type
// =============================================================================

interface MismatchItem {
  id: string;
  displayName: string;
  memberId: string | null;
  sourceType: string | null;
  accountId?: string | null;
  relatedEstateDocumentId?: string | null;
  sourceDocumentId?: string | null;
}

interface PairMismatch {
  hasMismatch: boolean;
  leftSource: string;
  rightSource: string;
  leftOnly: MismatchItem[];
  rightOnly: MismatchItem[];
}

interface TargetOption {
  id: string;
  label: string;
  context: Record<string, string>;
}

/** Derive all unique target options from mismatch pairs */
function deriveTargetOptions(pairs: PairMismatch[]): TargetOption[] {
  const seen = new Set<string>();
  const opts: TargetOption[] = [];
  for (const pair of pairs) {
    for (const entry of [...pair.leftOnly, ...pair.rightOnly]) {
      if (entry.accountId && !seen.has(`acct:${entry.accountId}`)) {
        seen.add(`acct:${entry.accountId}`);
        opts.push({ id: entry.accountId, label: `Account`, context: { accountId: entry.accountId } });
      }
      if (entry.relatedEstateDocumentId && !seen.has(`estate:${entry.relatedEstateDocumentId}`)) {
        seen.add(`estate:${entry.relatedEstateDocumentId}`);
        opts.push({ id: entry.relatedEstateDocumentId, label: `Estate`, context: { relatedEstateDocumentId: entry.relatedEstateDocumentId } });
      }
      if (entry.sourceDocumentId && entry.sourceType === 'insurance_policy' && !seen.has(`ins:${entry.sourceDocumentId}`)) {
        seen.add(`ins:${entry.sourceDocumentId}`);
        opts.push({ id: entry.sourceDocumentId, label: `Insurance`, context: { sourceDocumentId: entry.sourceDocumentId } });
      }
    }
  }
  return opts;
}

function MismatchBlock({ label, pairs, onAddManual, targetLabels }: {
  label: string;
  pairs: PairMismatch[];
  onAddManual?: (context: Record<string, string>) => void;
  targetLabels?: Record<string, Array<{ id: string; label: string }>>;
}) {
  if (!pairs || pairs.length === 0) return null;
  const [showPicker, setShowPicker] = useState(false);

  const scrollTo = (id: string) => {
    const el = document.getElementById(`ben-${id}`);
    if (el) {
      el.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
      el.classList.add('ring-2', 'ring-blue-400');
      setTimeout(() => el.classList.remove('ring-2', 'ring-blue-400'), 2000);
    }
  };

  const targets = deriveTargetOptions(pairs);

  // Enrich labels from targetLabels if available
  const enrichedTargets = targets.map((t) => {
    if (t.context.accountId && targetLabels?.accounts) {
      const found = targetLabels.accounts.find((a: any) => a.id === t.context.accountId);
      if (found) return { ...t, label: found.label };
    }
    if (t.context.relatedEstateDocumentId && targetLabels?.estateDocuments) {
      const found = targetLabels.estateDocuments.find((d: any) => d.id === t.context.relatedEstateDocumentId);
      if (found) return { ...t, label: found.label };
    }
    if (t.context.sourceDocumentId && targetLabels?.insuranceDocuments) {
      const found = targetLabels.insuranceDocuments.find((d: any) => d.id === t.context.sourceDocumentId);
      if (found) return { ...t, label: found.label };
    }
    return t;
  });

  return (
    <div className="mt-2 pt-2 border-t border-gray-100" data-testid={`mismatch-${label.toLowerCase()}`}>
      <div className="flex items-center justify-between mb-1">
        <p className="text-xs font-semibold text-gray-600">{label} Mismatches</p>
        {onAddManual && enrichedTargets.length === 1 && (
          <button className="text-xs text-blue-600 hover:underline" onClick={() => onAddManual(enrichedTargets[0].context)}>
            + Add to {enrichedTargets[0].label}
          </button>
        )}
        {onAddManual && enrichedTargets.length > 1 && !showPicker && (
          <button className="text-xs text-blue-600 hover:underline" onClick={() => setShowPicker(true)}>
            + Add beneficiary...
          </button>
        )}
      </div>
      {showPicker && enrichedTargets.length > 1 && (
        <div className="mb-2 flex flex-wrap gap-1" data-testid="target-picker">
          <span className="text-xs text-gray-500">Attach to:</span>
          {enrichedTargets.map((t) => (
            <button
              key={t.id}
              className="px-2 py-0.5 text-xs bg-blue-50 text-blue-700 rounded hover:bg-blue-100"
              onClick={() => { onAddManual!(t.context); setShowPicker(false); }}
            >
              {t.label}
            </button>
          ))}
          <button className="text-xs text-gray-400 hover:text-gray-600" onClick={() => setShowPicker(false)}>Cancel</button>
        </div>
      )}
      {pairs.map((pair) => (
        <div key={`${pair.leftSource}-${pair.rightSource}`} className="mb-1.5">
          <p className="text-xs text-gray-500 capitalize">{pair.leftSource} ↔ {pair.rightSource}</p>
          <div className="ml-3 text-xs space-y-0.5">
            {pair.leftOnly.map((b) => (
              <button key={b.id} onClick={() => scrollTo(b.id)} className="block text-left text-red-600 hover:underline cursor-pointer">
                {pair.leftSource} only: {b.displayName}
              </button>
            ))}
            {pair.rightOnly.map((b) => (
              <button key={b.id} onClick={() => scrollTo(b.id)} className="block text-left text-red-600 hover:underline cursor-pointer">
                {pair.rightSource} only: {b.displayName}
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// =============================================================================
// PrioritySelect — inline priority classifier
// =============================================================================

function PrioritySelect({ benId, value, onUpdate }: { benId: string; value: string; onUpdate: (args: { benId: string; priorityType: string }) => void }) {
  return (
    <select
      value={value}
      onChange={(e) => onUpdate({ benId, priorityType: e.target.value })}
      className={`px-1.5 py-0.5 rounded text-xs font-medium border-0 cursor-pointer ${PRIORITY_COLORS[value] ?? PRIORITY_COLORS.unspecified}`}
    >
      {PRIORITY_OPTIONS.map((o) => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  );
}

// =============================================================================
// DeleteConfirm — inline confirm before deleting a manual row
// =============================================================================

function DeleteConfirm({ benId, isTarget, onConfirm, onCancel }: { benId: string; isTarget: boolean; onConfirm: () => void; onCancel: () => void }) {
  if (!isTarget) return null;
  return (
    <div className="mt-1 flex items-center gap-2 p-1.5 bg-red-50 rounded text-xs" data-testid={`confirm-delete-${benId}`}>
      <span className="text-red-700">Remove this beneficiary?</span>
      <button className="px-2 py-0.5 text-white bg-red-600 rounded hover:bg-red-700" onClick={onConfirm}>Confirm</button>
      <button className="text-gray-500 hover:text-gray-700" onClick={onCancel}>Cancel</button>
    </div>
  );
}

// =============================================================================
// Signal chip — tiny captured/not-captured indicator
// =============================================================================

function Signal({ label, ok }: { label: string; ok: boolean }) {
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full ${ok ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
      {ok ? <CheckCircle className="h-3 w-3" /> : <AlertCircle className="h-3 w-3" />}
      {label}
    </span>
  );
}

// =============================================================================
// CreateAndLinkForm — extracted for clarity
// =============================================================================

function CreateAndLinkForm({
  benId: _benId,
  createName,
  setCreateName,
  createRelationship,
  setCreateRelationship,
  duplicateMatch,
  serverDuplicate,
  onLink,
  onCreateAndLink,
  isCreating,
  onCancel,
}: {
  benId: string;
  createName: string;
  setCreateName: (v: string) => void;
  createRelationship: string;
  setCreateRelationship: (v: string) => void;
  duplicateMatch: { id: string; name: string; relationship: string } | null;
  serverDuplicate: { id: string; name: string; relationship: string } | null;
  onLink: (memberId: string) => void;
  onCreateAndLink: () => void;
  isCreating: boolean;
  onCancel: () => void;
}) {
  const dup = duplicateMatch ?? serverDuplicate;

  return (
    <div className="mt-2 p-3 bg-gray-50 rounded-lg space-y-2">
      <p className="text-xs font-medium text-gray-600">Create new household member</p>
      <div className="flex gap-2">
        <input
          type="text"
          value={createName}
          onChange={(e) => setCreateName(e.target.value)}
          placeholder="Full name"
          className="border border-gray-300 rounded px-2 py-1 text-sm flex-1"
        />
        <select
          value={createRelationship}
          onChange={(e) => setCreateRelationship(e.target.value)}
          className="border border-gray-300 rounded px-2 py-1 text-sm"
        >
          <option value="" disabled>Relationship...</option>
          {RELATIONSHIP_OPTIONS.map((r) => (
            <option key={r.value} value={r.value}>{r.label}</option>
          ))}
        </select>
      </div>

      {dup ? (
        <div className="flex items-start gap-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs">
          <AlertTriangle className="h-3.5 w-3.5 text-yellow-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-yellow-800 font-medium">
              "{dup.name}" ({dup.relationship}) already exists in your household.
            </p>
            <p className="text-yellow-700 mt-0.5">
              Link to this existing member instead of creating a duplicate.
            </p>
            <button
              className="mt-1.5 inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-blue-600 bg-white border border-blue-200 rounded hover:bg-blue-50"
              onClick={() => onLink(dup.id)}
            >
              <Link2 className="h-3 w-3" /> Link to {dup.name}
            </button>
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <button
            className="inline-flex items-center gap-1 px-3 py-1 text-xs font-medium text-white bg-green-600 rounded hover:bg-green-700 disabled:opacity-50"
            disabled={!createName.trim() || !createRelationship || isCreating}
            onClick={onCreateAndLink}
          >
            {isCreating ? 'Creating...' : 'Create & Link'}
          </button>
          <button className="text-xs text-gray-400 hover:text-gray-600" onClick={onCancel}>Cancel</button>
        </div>
      )}
    </div>
  );
}
